// Guard against react-router v7 pushing duplicate history entries for a single navigate() call.
// Without this, browser back requires multiple clicks to go back one logical step.
// Apparently this fix has to execute before the app renders so it lives above the imports.
{
	const origPush = history.pushState.bind(history);
	let lastPushUrl: unknown = null;
	let lastPushTime = 0;
	history.pushState = function (...args: Parameters<typeof origPush>) {
		const url = args[2];
		const now = Date.now();
		if (url === lastPushUrl && now - lastPushTime < 100) return;
		lastPushUrl = url;
		lastPushTime = now;
		return origPush(...args);
	};
}

import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { toast } from 'react-toastify';
import App from './App';
import './index.css';
import './polling/StreamSync';
import store from './redux/store';
import { mixpanelTrackEvent } from './utils';

// Only initialize Sentry in production environments
if (import.meta.env.PROD) {
	Sentry.init({
		dsn: 'https://1e9531568ec390da6f9b92e5acd9ac1e@o4509849084690432.ingest.us.sentry.io/4510049205420032',
		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: true,
		// Release tracking for source map association
		release: import.meta.env.VITE_GIT_SHA || 'development',
	});
}

// Override toast functions to automatically log to Sentry (only in production)
// Believe it or not in JS you can just add things to these global functions. This is actually clean. Feels a bit dangerous -- hoping it's not! - ss
const originalError = toast.error;
const originalWarning = toast.warning;

toast.error = (message, options) => {
	Sentry.captureException(new Error(`Toast Error: ${message}`));
	mixpanelTrackEvent('Toast Error', {
		source: 'studio',
		error_message: typeof message === 'string' ? message : 'Unknown error',
	});
	return originalError(message, options);
};

toast.warning = (message, options) => {
	Sentry.captureMessage(`Toast Warning: ${message}`, 'warning');
	return originalWarning(message, options);
};

let app = (
	<Provider store={store}>
		<App />
	</Provider>
);

// Wait for redirect.js to load before rendering the app
function waitForRedirect() {
	return new Promise<void>((resolve) => {
		if ((window as any).redirectLoaded) {
			resolve();
			return;
		}

		let timeout: ReturnType<typeof setTimeout>;
		let checkInterval: ReturnType<typeof setInterval>;

		checkInterval = setInterval(() => {
			if ((window as any).redirectLoaded) {
				clearInterval(checkInterval);
				clearTimeout(timeout);
				resolve();
			}
		}, 40);

		// Timeout after 3 seconds
		timeout = setTimeout(() => {
			clearInterval(checkInterval);
			resolve();
		}, 3000);
	});
}

waitForRedirect().then(() => {
	const rootElement = document.getElementById('root');
	if (rootElement) {
		createRoot(rootElement).render(<React.StrictMode>{app}</React.StrictMode>);
	} else {
		console.error('Root element not found');
	}
});
